import axios from 'axios';
import qs from 'querystring';
import { ElMessage } from 'element-plus';
import Cookies from 'js-cookie';
import router from '@/router';

const instance = axios.create({
    // 发送请求的公共基础路径接口位置
    baseURL: process.env.VUE_APP_BASE_API,
    timeout: 55000, // 请求超时时间
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json;charset=utf-8'
    }
});

// 添加请求拦截器
instance.interceptors.request.use(
    config => {
        console.log('发起请求:', config.url);
        
        // 从所有可能的存储位置获取token
        const adminToken = window.sessionStorage.getItem('token') || 
                      window.localStorage.getItem('token') ||
                      Cookies.get('token');
        const employeeToken =
            window.sessionStorage.getItem('points_employee_token') ||
            window.localStorage.getItem('points_employee_token');

        const url = config.url || ''
        // 仅「换票/拉授权/主题资源」接口不带 token；auth/me 必须带员工 token
        const isPointsLoginApi = /\/points_mall\/(auth\/(wecom|dev)\/|theme\/)/.test(url)
        // 管理端积分商城接口（用管理员 token）
        // account/ 含 search、adjust、export、import、import/template
        const isPointsAdminApi = /\/points_mall\/(account\/|record\/|product\/|order\/|story\/(search|save|shelf|upload)|improve\/(search|save|shelf|upload|comments\/search|comment\/delete|type_reviewer|export)|expire\/|admin\/|perms\/|auth\/admin\/)/.test(url)
        // 员工端业务接口（含 story/like、improve/like、jssdk、看板/详情）
        const isPointsEmployeeApi = /\/points_mall\//.test(url) && !isPointsLoginApi && !isPointsAdminApi

        let token = null
        if (isPointsLoginApi) {
            token = null
        } else if (isPointsEmployeeApi) {
            // 员工端接口只用员工 token，禁止回落管理员 token（否则会误报登录失效）
            token = employeeToken || null
        } else {
            token = adminToken || null
        }
        
        if (token) {
            // 在请求头中添加token
            config.headers.Authorization = `Bearer ${token}`;
            console.log('请求添加token:', token.substring(0, 15) + '...');
            
            // 确保token保存在所有存储位置
            if (!isPointsEmployeeApi) {
                window.sessionStorage.setItem('token', token);
                window.localStorage.setItem('token', token);
                // 当请求发起时，如果有token但Cookies中没有，也添加到Cookies中
                if (!Cookies.get('token')) {
                    Cookies.set('token', token, { expires: 1 }); // 1天过期
                }
            }
            
            // 如果请求头中已包含Authorization，进行说明
            if (config.headers.Authorization && config.headers.Authorization !== `Bearer ${token}`) {
                console.log('请求已有Authorization，被替换:', config.headers.Authorization);
            }
        } else {
            console.log('请求未添加token，未找到token');
            
            // 如果是登录或验证码等不需要token的接口，不显示警告
            if (!config.url.includes('/login/') && !config.url.includes('/captcha/') && !config.url.includes('/points_mall/auth/')) {
                console.warn('警告: 发送需授权请求但没有token');
            }
        }
        
        // 如果是FormData类型的数据，自动修改Content-Type
        if (config.data instanceof FormData) {
            config.headers['Content-Type'] = 'multipart/form-data';
        }
        
        // 添加请求ID用于跟踪
        const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        config.requestId = requestId;
        console.log(`请求[${requestId}]准备发送:`, {
            url: config.url,
            method: config.method,
            hasToken: !!token,
            data: config.data ? '有数据' : '无数据'
        });

        return config;
    },
    error => {
        console.log('请求拦截器错误:', error);
        return Promise.reject(error);
    }
);

// 添加响应拦截器
instance.interceptors.response.use(
    response => {
        console.log('API响应:', response.config.url, response.data);
        
        // 检查响应状态码
        const res = response.data;
        
        // 仅 token 失效(401)才踢登录；403 是业务无权限，不能清会话
        if (res.code === 401) {
            const reqUrl = response.config?.url || ''
            // 换票/拉授权接口的 401 交给业务处理，不踢登录
            if (/\/points_mall\/(auth\/(wecom|dev)\/|theme\/)/.test(reqUrl)) {
                return Promise.reject(new Error(res.msg || '鉴权失败'))
            }
            const isPointsAdminApi = /\/points_mall\/(account\/|record\/|product\/|order\/|story\/(search|save|shelf|upload)|improve\/(search|save|shelf|upload|export)|expire\/|admin\/|perms\/|auth\/admin\/)/.test(reqUrl)
            const isPointsEmployeeApi = /\/points_mall\//.test(reqUrl) && !isPointsAdminApi
            console.log('登录已过期，请重新登录', isPointsEmployeeApi ? '(员工端)' : '(管理端)');

            if (isPointsEmployeeApi) {
                // 员工端：清员工 token，留在 /points 重新一键登录（不要整页硬跳造成循环）
                window.sessionStorage.removeItem('points_employee_token');
                window.localStorage.removeItem('points_employee_token');
                // 已在员工页则提示重新登录；勿跳管理端 /login
                if (!window.location.pathname.startsWith('/points')) {
                    setTimeout(() => {
                        window.location.href = '/points';
                    }, 800);
                }
                return Promise.reject(new Error(res.msg || '登录已失效，请重新登录'));
            } else {
                // 清除所有存储位置的token和用户信息
                window.sessionStorage.removeItem('token');
                window.localStorage.removeItem('token');
                Cookies.remove('token');
                
                // 清除用户信息
                window.sessionStorage.removeItem('userSerializer');
                window.localStorage.removeItem('userSerializer');
                window.sessionStorage.removeItem('menuList');
                window.localStorage.removeItem('menuList');
                window.sessionStorage.removeItem('perms');
                window.localStorage.removeItem('perms');
                window.sessionStorage.removeItem('roles');
                window.localStorage.removeItem('roles');
                
                setTimeout(() => {
                    window.location.href = '/login';
                }, 1500);
            }
            return Promise.reject(new Error(res.msg || '登录已过期，请重新登录'));
        }
        if (res.code === 403) {
            return Promise.reject(new Error(res.msg || '无权限'))
        }
        
        // 直接返回原始响应，让业务代码处理
        return response;
    },
    error => {
        console.log('请求错误:', error);
        let message = error.message || '请求失败';
        
        if (error.response) {
            // 根据状态码处理
            switch (error.response.status) {
                case 401:
                    message = '登录已过期，请重新登录';
                    // 清除所有存储位置的token和用户信息
                    window.sessionStorage.removeItem('token');
                    window.localStorage.removeItem('token');
                    Cookies.remove('token');
                    
                    // 清除用户信息
                    window.sessionStorage.removeItem('userSerializer');
                    window.localStorage.removeItem('userSerializer');
                    window.sessionStorage.removeItem('menuList');
                    window.localStorage.removeItem('menuList');
                    window.sessionStorage.removeItem('perms');
                    window.localStorage.removeItem('perms');
                    window.sessionStorage.removeItem('roles');
                    window.localStorage.removeItem('roles');
                    
                    setTimeout(() => {
                        window.location.href = '/login';
                    }, 1500);
                    break;
                case 404:
                    message = '请求的资源不存在';
                    break;
                case 500:
                    message = '服务器内部错误';
                    break;
                default:
                    message = error.response.data.message || '请求失败';
            }
        } else if (error.request) {
            message = '网络请求超时，请检查网络连接';
        }
        
        ElMessage.error(message);
        return Promise.reject(error);
    }
);

// get 请求
export const get = async (url, params = {}) => {
    try {
        const response = await instance.get(url, { params });
        return response.data;
    } catch (error) {
        console.error('GET 请求错误：', error);
        throw error;
    }
};

// post 请求
export const post = async (url, data = {}) => {
    console.log('进入这个post', url, data);
    try {
        const response = await instance.post(url, data);
        return response.data;
    } catch (error) {
        console.error('POST 请求错误：', error);
        throw error;
    }
};

// 删除请求
export const del = async (url, params = {}) => {
    try {
        const response = await instance.delete(url, { params });
        return response.data;
    } catch (error) {
        console.error('DELETE 请求错误：', error);
        throw error;
    }
};

// 文件上传请求
export const fileUpload = async (url, params = {}) => {
    try {
        const response = await instance.post(url, params, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    } catch (error) {
        console.error('文件上传请求错误：', error);
        throw error;
    }
};

export default instance;